import React, { useState, useEffect, useCallback } from 'react';
import SuperAdminLayout from '@/components/superadmin/SuperAdminLayout';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Building2, Plus, Search, Eye, Pencil, PauseCircle, PlayCircle, Trash2, X, ExternalLink, Mail, RotateCw, KeyRound } from 'lucide-react';
import { toast } from 'sonner';
import { API } from '@/utils/api';
import axios from 'axios';

const STATUS_COLORS = {
  actif: 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20',
  essai: 'bg-blue-500/10 text-blue-400 border-blue-500/20',
  suspendu: 'bg-red-500/10 text-red-400 border-red-500/20',
  expire: 'bg-amber-500/10 text-amber-400 border-amber-500/20',
};
const STATUS_LABELS = { actif: 'Actif', essai: 'Essai', suspendu: 'Suspendu', expire: 'Expire' };
const PLAN_LABELS = { trial: 'Essai', starter: 'Starter', business: 'Business', enterprise: 'Enterprise', enterprise_plus: 'Enterprise+' };
const PLAN_COLORS = { trial: 'text-slate-400', starter: 'text-blue-400', business: 'text-indigo-400', enterprise: 'text-purple-400', enterprise_plus: 'text-amber-400' };

const SWISS_CANTONS = [
  'AG', 'AI', 'AR', 'BE', 'BL', 'BS', 'FR', 'GE', 'GL', 'GR',
  'JU', 'LU', 'NE', 'NW', 'OW', 'SG', 'SH', 'SO', 'SZ', 'TG',
  'TI', 'UR', 'VD', 'VS', 'ZG', 'ZH'
];
const CANTON_NAMES = {
  AG: 'Argovie', AI: 'Appenzell Rh.-Int.', AR: 'Appenzell Rh.-Ext.', BE: 'Berne', BL: 'Bale-Campagne', BS: 'Bale-Ville',
  FR: 'Fribourg', GE: 'Geneve', GL: 'Glaris', GR: 'Grisons', JU: 'Jura', LU: 'Lucerne', NE: 'Neuchatel',
  NW: 'Nidwald', OW: 'Obwald', SG: 'Saint-Gall', SH: 'Schaffhouse', SO: 'Soleure', SZ: 'Schwyz',
  TG: 'Thurgovie', TI: 'Tessin', UR: 'Uri', VD: 'Vaud', VS: 'Valais', ZG: 'Zoug', ZH: 'Zurich'
};

const emptyForm = { company_name: '', contact_first_name: '', contact_last_name: '', contact_name: '', contact_email: '', contact_phone: '', address: '', postal_code: '', city: '', canton: '', tva_number: '', plan: 'trial', billing_cycle: 'monthly', trial_days: 14, notes: '' };

const SuperAdminTenants = ({ auth, onLogout }) => {
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterPlan, setFilterPlan] = useState('all');
  const [showForm, setShowForm] = useState(false);
  const [editingTenant, setEditingTenant] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [showDetail, setShowDetail] = useState(null);
  const [detailData, setDetailData] = useState(null);
  const [showSubDialog, setShowSubDialog] = useState(false);
  const [subForm, setSubForm] = useState({ plan: '', billing_cycle: 'monthly' });

  const loadTenants = useCallback(async () => {
    try {
      const params = {};
      if (filterStatus !== 'all') params.status = filterStatus;
      if (filterPlan !== 'all') params.plan = filterPlan;
      if (search) params.search = search;
      const res = await axios.get(`${API}/saas/tenants`, { params });
      setTenants(res.data);
    } catch (err) { console.error(err); } finally { setLoading(false); }
  }, [filterStatus, filterPlan, search]);

  useEffect(() => { loadTenants(); }, [loadTenants]);

  const loadDetail = async (id) => {
    try {
      const res = await axios.get(`${API}/saas/tenants/${id}`);
      setDetailData(res.data);
      setShowDetail(id);
    } catch (err) { toast.error('Erreur chargement détail'); }
  };

  const openCreate = () => { setEditingTenant(null); setForm(emptyForm); setShowForm(true); };
  const openEdit = (t) => {
    setEditingTenant(t);
    setForm({
      company_name: t.company_name || '',
      contact_first_name: t.contact_first_name || '',
      contact_last_name: t.contact_last_name || '',
      contact_name: t.contact_name || '',
      contact_email: t.contact_email || '',
      contact_phone: t.contact_phone || '',
      address: t.address || '',
      postal_code: t.postal_code || '',
      city: t.city || '',
      canton: t.canton || '',
      tva_number: t.tva_number || '',
      plan: t.plan,
      billing_cycle: t.billing_cycle || 'monthly',
      trial_days: 14,
      notes: t.notes || '',
    });
    setShowForm(true);
  };

  const handleSave = async () => {
    if (!form.company_name || !form.contact_email) { toast.error('Nom entreprise et email requis'); return; }
    if (!form.contact_first_name && !form.contact_last_name && !form.contact_name) { toast.error('Nom du contact requis'); return; }
    setSaving(true);
    try {
      if (editingTenant) {
        await axios.put(`${API}/saas/tenants/${editingTenant.id}`, {
          company_name: form.company_name, contact_first_name: form.contact_first_name, contact_last_name: form.contact_last_name,
          contact_name: form.contact_name || `${form.contact_first_name} ${form.contact_last_name}`.trim(),
          contact_email: form.contact_email, contact_phone: form.contact_phone,
          address: form.address, postal_code: form.postal_code, city: form.city, canton: form.canton,
          tva_number: form.tva_number, notes: form.notes
        });
        toast.success('Entreprise modifiee');
      } else {
        const res = await axios.post(`${API}/saas/tenants`, form);
        const tempPwd = res.data?.temp_password;
        if (tempPwd) {
          toast.success(`Entreprise creee ! Mot de passe temporaire : ${tempPwd}`, { duration: 15000 });
        } else {
          toast.success('Entreprise creee');
        }
      }
      setShowForm(false);
      loadTenants();
    } catch (err) { toast.error(err.response?.data?.detail || 'Erreur'); } finally { setSaving(false); }
  };

  const handleSuspend = async (id) => {
    if (!window.confirm('Suspendre cette entreprise ?')) return;
    try {
      await axios.post(`${API}/saas/tenants/${id}/suspend`);
      toast.success('Entreprise suspendue');
      loadTenants();
      if (showDetail === id) loadDetail(id);
    } catch (err) { toast.error('Erreur suspension'); }
  };

  const handleReactivate = async (id) => {
    try {
      await axios.post(`${API}/saas/tenants/${id}/reactivate`);
      toast.success('Entreprise réactivée');
      loadTenants();
      if (showDetail === id) loadDetail(id);
    } catch (err) { toast.error('Erreur réactivation'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer definitivement cette entreprise ?')) return;
    try {
      await axios.delete(`${API}/saas/tenants/${id}`);
      toast.success('Entreprise supprimee');
      setShowDetail(null);
      loadTenants();
    } catch (err) { toast.error('Erreur suppression'); }
  };

  const handleImpersonate = async (id, companyName) => {
    try {
      const res = await axios.post(`${API}/saas/tenants/${id}/impersonate`);
      const { access_token, tenant } = res.data;
      // Open admin dashboard in new tab with impersonated session
      const adminUrl = window.location.origin + '/admin/login?impersonate=' + encodeURIComponent(JSON.stringify({ token: access_token, tenant }));
      window.open(adminUrl, '_blank');
      toast.success(`Session test ouverte pour ${companyName}`);
    } catch (err) { toast.error('Erreur ouverture session test'); }
  };

  const handleResendEmail = async (id, companyName) => {
    if (!window.confirm(`Renvoyer l'email de bienvenue avec un NOUVEAU mot de passe pour ${companyName} ?`)) return;
    try {
      const res = await axios.post(`${API}/saas/tenants/${id}/resend-welcome`);
      toast.success(`Email renvoye ! Nouveau mot de passe : ${res.data.temp_password}`);
    } catch (err) { toast.error('Erreur renvoi email'); }
  };

  const handleResetPassword = async (id, companyName) => {
    if (!window.confirm(`Reinitialiser le mot de passe pour ${companyName} ?`)) return;
    try {
      const res = await axios.post(`${API}/saas/tenants/${id}/reset-password`);
      const { email, new_password, email_sent } = res.data;
      if (email_sent) {
        toast.success(`Mot de passe reinitialise et envoye par email a ${email}`);
      } else {
        toast.success(
          <div>
            <strong>Mot de passe reinitialise</strong><br/>
            <span style={{color:'#ef4444'}}>Email non envoye - communiquez le mot de passe manuellement :</span><br/>
            Email : {email}<br/>
            Mot de passe : <code style={{background:'#f1f5f9',padding:'2px 6px',borderRadius:'4px',fontWeight:'bold'}}>{new_password}</code>
          </div>,
          { duration: 60000 }
        );
      }
    } catch (err) { toast.error('Erreur reinitialisation'); }
  };

  const openChangePlan = (tenant) => {
    setSubForm({ plan: tenant.plan || 'starter', billing_cycle: tenant.billing_cycle || 'monthly' });
    setShowSubDialog(tenant.id);
  };

  const handleChangePlan = async () => {
    try {
      await axios.put(`${API}/saas/tenants/${showSubDialog}/subscription`, subForm);
      toast.success('Abonnement modifié');
      setShowSubDialog(false);
      loadTenants();
      if (showDetail) loadDetail(showDetail);
    } catch (err) { toast.error(err.response?.data?.detail || 'Erreur'); }
  };

  const formatDate = (iso) => { if (!iso) return '-'; return new Date(iso).toLocaleDateString('fr-CH'); };
  const formatCHF = (v) => `CHF ${(v || 0).toFixed(2)}`;

  return (
    <SuperAdminLayout auth={auth} onLogout={onLogout}>
      <div className="p-6 lg:p-8 space-y-6" data-testid="superadmin-tenants">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-white">Entreprises</h1>
            <p className="text-sm text-slate-400">Gestion des clients SaaS</p>
          </div>
          <Button onClick={openCreate} className="bg-indigo-600 hover:bg-indigo-700 text-white" data-testid="create-tenant-button">
            <Plus className="w-4 h-4 mr-2" /> Nouvelle entreprise
          </Button>
        </div>

        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
            <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Rechercher..." className="pl-10 bg-slate-900 border-slate-700 text-white placeholder:text-slate-500" data-testid="tenant-search-input" />
          </div>
          <Select value={filterStatus} onValueChange={setFilterStatus}>
            <SelectTrigger className="w-40 bg-slate-900 border-slate-700 text-white" data-testid="filter-status">
              <SelectValue placeholder="Statut" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tous statuts</SelectItem>
              <SelectItem value="actif">Actif</SelectItem>
              <SelectItem value="essai">Essai</SelectItem>
              <SelectItem value="suspendu">Suspendu</SelectItem>
              <SelectItem value="expire">Expiré</SelectItem>
            </SelectContent>
          </Select>
          <Select value={filterPlan} onValueChange={setFilterPlan}>
            <SelectTrigger className="w-40 bg-slate-900 border-slate-700 text-white" data-testid="filter-plan">
              <SelectValue placeholder="Plan" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tous plans</SelectItem>
              <SelectItem value="trial">Essai</SelectItem>
              <SelectItem value="starter">Starter</SelectItem>
              <SelectItem value="business">Business</SelectItem>
              <SelectItem value="enterprise">Enterprise</SelectItem>
              <SelectItem value="enterprise_plus">Enterprise+</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Table */}
        {loading ? (
          <div className="text-slate-400 text-center py-20">Chargement...</div>
        ) : tenants.length === 0 ? (
          <Card className="bg-slate-900 border-slate-800 p-12 text-center">
            <Building2 className="w-12 h-12 text-slate-600 mx-auto mb-3" />
            <p className="text-slate-400">Aucune entreprise trouvée</p>
          </Card>
        ) : (
          <div className="overflow-x-auto rounded-xl border border-slate-800">
            <table className="w-full text-sm" data-testid="tenants-table">
              <thead>
                <tr className="bg-slate-900/50 border-b border-slate-800">
                  <th className="text-left py-3 px-4 text-xs font-medium text-slate-400 uppercase tracking-wider">Entreprise</th>
                  <th className="text-left py-3 px-4 text-xs font-medium text-slate-400 uppercase tracking-wider hidden md:table-cell">Contact</th>
                  <th className="text-left py-3 px-4 text-xs font-medium text-slate-400 uppercase tracking-wider">Plan</th>
                  <th className="text-left py-3 px-4 text-xs font-medium text-slate-400 uppercase tracking-wider">Statut</th>
                  <th className="text-left py-3 px-4 text-xs font-medium text-slate-400 uppercase tracking-wider hidden lg:table-cell">Fin abonnement</th>
                  <th className="text-right py-3 px-4 text-xs font-medium text-slate-400 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody>
                {tenants.map((t) => (
                  <tr key={t.id} className="border-b border-slate-800/50 hover:bg-slate-900/30 transition-colors">
                    <td className="py-3 px-4">
                      <p className="font-medium text-white">{t.company_name}</p>
                      <p className="text-xs text-slate-500">{t.city}</p>
                    </td>
                    <td className="py-3 px-4 hidden md:table-cell">
                      <p className="text-slate-300">{t.contact_name}</p>
                      <p className="text-xs text-slate-500">{t.contact_email}</p>
                    </td>
                    <td className="py-3 px-4">
                      <span className={`text-sm font-medium ${PLAN_COLORS[t.plan] || 'text-slate-400'}`}>{PLAN_LABELS[t.plan] || t.plan}</span>
                      <p className="text-xs text-slate-600">{t.billing_cycle === 'yearly' ? 'Annuel' : 'Mensuel'}</p>
                    </td>
                    <td className="py-3 px-4">
                      <span className={`text-xs px-2 py-1 rounded-full border ${STATUS_COLORS[t.status] || 'bg-slate-800 text-slate-400 border-slate-700'}`}>{STATUS_LABELS[t.status] || t.status}</span>
                    </td>
                    <td className="py-3 px-4 text-slate-400 text-xs hidden lg:table-cell">{formatDate(t.subscription?.end_date)}</td>
                    <td className="py-3 px-4 text-right">
                      <div className="flex items-center justify-end gap-1">
                        <Button variant="ghost" size="sm" onClick={() => loadDetail(t.id)} className="text-slate-400 hover:text-white" data-testid={`view-tenant-${t.id}`} title="Voir details">
                          <Eye className="w-4 h-4" />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => openEdit(t)} className="text-slate-400 hover:text-white" data-testid={`edit-tenant-${t.id}`} title="Modifier">
                          <Pencil className="w-4 h-4" />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => handleResendEmail(t.id, t.company_name)} className="text-blue-400 hover:text-blue-300" data-testid={`resend-email-${t.id}`} title="Renvoyer email">
                          <Mail className="w-4 h-4" />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => handleResetPassword(t.id, t.company_name)} className="text-amber-400 hover:text-amber-300" data-testid={`reset-pw-${t.id}`} title="Reinitialiser mot de passe">
                          <KeyRound className="w-4 h-4" />
                        </Button>
                        {t.status === 'suspendu' ? (
                          <Button variant="ghost" size="sm" onClick={() => handleReactivate(t.id)} className="text-emerald-400 hover:text-emerald-300" title="Reactiver">
                            <PlayCircle className="w-4 h-4" />
                          </Button>
                        ) : t.status !== 'supprime' && (
                          <Button variant="ghost" size="sm" onClick={() => handleSuspend(t.id)} className="text-amber-400 hover:text-amber-300" title="Suspendre">
                            <PauseCircle className="w-4 h-4" />
                          </Button>
                        )}
                        <Button variant="ghost" size="sm" onClick={() => handleDelete(t.id)} className="text-red-400 hover:text-red-300" data-testid={`delete-tenant-${t.id}`} title="Supprimer">
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Create / Edit Dialog */}
      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent className="bg-slate-900 border-slate-700 text-white max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-white text-lg">{editingTenant ? 'Modifier l\'entreprise' : 'Nouvelle entreprise'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-5 mt-2">
            {/* Entreprise */}
            <div>
              <p className="text-xs uppercase tracking-wider text-slate-400 font-semibold mb-3">Entreprise</p>
              <div className="grid grid-cols-3 gap-3">
                <div className="col-span-2">
                  <Label className="text-slate-300 text-xs">Raison sociale *</Label>
                  <Input value={form.company_name} onChange={(e) => setForm({ ...form, company_name: e.target.value })} placeholder="Nom de la societe" className="bg-slate-800 border-slate-700 text-white mt-1" data-testid="tenant-company-name" />
                </div>
                <div>
                  <Label className="text-slate-300 text-xs">No TVA</Label>
                  <Input value={form.tva_number} onChange={(e) => setForm({ ...form, tva_number: e.target.value })} placeholder="CHE-123.456.789" className="bg-slate-800 border-slate-700 text-white mt-1" data-testid="tenant-tva-number" />
                </div>
              </div>
            </div>

            {/* Adresse */}
            <div>
              <p className="text-xs uppercase tracking-wider text-slate-400 font-semibold mb-3">Adresse</p>
              <div className="grid grid-cols-4 gap-3">
                <div className="col-span-4">
                  <Label className="text-slate-300 text-xs">Rue et numero</Label>
                  <Input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} placeholder="Route de Neuchatel 13" className="bg-slate-800 border-slate-700 text-white mt-1" data-testid="tenant-address" />
                </div>
                <div>
                  <Label className="text-slate-300 text-xs">NPA</Label>
                  <Input value={form.postal_code} onChange={(e) => setForm({ ...form, postal_code: e.target.value })} placeholder="2520" className="bg-slate-800 border-slate-700 text-white mt-1" data-testid="tenant-postal-code" />
                </div>
                <div className="col-span-2">
                  <Label className="text-slate-300 text-xs">Localite</Label>
                  <Input value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} placeholder="La Neuveville" className="bg-slate-800 border-slate-700 text-white mt-1" data-testid="tenant-city" />
                </div>
                <div>
                  <Label className="text-slate-300 text-xs">Canton</Label>
                  <Select value={form.canton} onValueChange={(v) => setForm({ ...form, canton: v })}>
                    <SelectTrigger className="bg-slate-800 border-slate-700 text-white mt-1" data-testid="tenant-canton-select">
                      <SelectValue placeholder="Choisir..." />
                    </SelectTrigger>
                    <SelectContent>
                      {SWISS_CANTONS.map(c => (
                        <SelectItem key={c} value={c}>{c} — {CANTON_NAMES[c]}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>

            {/* Personne de contact */}
            <div>
              <p className="text-xs uppercase tracking-wider text-slate-400 font-semibold mb-3">Personne de contact</p>
              <div className="grid grid-cols-4 gap-3">
                <div>
                  <Label className="text-slate-300 text-xs">Prenom *</Label>
                  <Input value={form.contact_first_name} onChange={(e) => setForm({ ...form, contact_first_name: e.target.value })} placeholder="Jean" className="bg-slate-800 border-slate-700 text-white mt-1" data-testid="tenant-contact-first-name" />
                </div>
                <div>
                  <Label className="text-slate-300 text-xs">Nom *</Label>
                  <Input value={form.contact_last_name} onChange={(e) => setForm({ ...form, contact_last_name: e.target.value })} placeholder="Dupont" className="bg-slate-800 border-slate-700 text-white mt-1" data-testid="tenant-contact-last-name" />
                </div>
                <div>
                  <Label className="text-slate-300 text-xs">Email *</Label>
                  <Input value={form.contact_email} onChange={(e) => setForm({ ...form, contact_email: e.target.value })} type="email" placeholder="jean@garage.ch" className="bg-slate-800 border-slate-700 text-white mt-1" data-testid="tenant-contact-email" />
                </div>
                <div>
                  <Label className="text-slate-300 text-xs">Telephone</Label>
                  <Input value={form.contact_phone} onChange={(e) => setForm({ ...form, contact_phone: e.target.value })} placeholder="+41 32 751 22 04" className="bg-slate-800 border-slate-700 text-white mt-1" data-testid="tenant-contact-phone" />
                </div>
              </div>
            </div>

            {/* Abonnement */}
            {!editingTenant && (
              <div>
                <p className="text-xs uppercase tracking-wider text-slate-400 font-semibold mb-3">Abonnement</p>
                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <Label className="text-slate-300 text-xs">Plan</Label>
                    <Select value={form.plan} onValueChange={(v) => setForm({ ...form, plan: v })}>
                      <SelectTrigger className="bg-slate-800 border-slate-700 text-white mt-1" data-testid="tenant-plan-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="trial">Essai Gratuit (14 jours)</SelectItem>
                        <SelectItem value="starter">Starter — CHF 89/mois</SelectItem>
                        <SelectItem value="business">Business — CHF 189/mois</SelectItem>
                        <SelectItem value="enterprise">Enterprise — CHF 349/mois</SelectItem>
                        <SelectItem value="enterprise_plus">Enterprise+ — Sur demande</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-slate-300 text-xs">Cycle de facturation</Label>
                    <Select value={form.billing_cycle} onValueChange={(v) => setForm({ ...form, billing_cycle: v })}>
                      <SelectTrigger className="bg-slate-800 border-slate-700 text-white mt-1" data-testid="tenant-cycle-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="monthly">Mensuel</SelectItem>
                        <SelectItem value="yearly">Annuel (2 mois offerts)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  {form.plan === 'trial' && (
                    <div>
                      <Label className="text-slate-300 text-xs">Jours d'essai</Label>
                      <Input type="number" value={form.trial_days} onChange={(e) => setForm({ ...form, trial_days: parseInt(e.target.value) || 14 })} className="bg-slate-800 border-slate-700 text-white mt-1" />
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Notes */}
            <div>
              <Label className="text-slate-300 text-xs">Notes internes</Label>
              <textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Remarques, informations complementaires..." className="w-full bg-slate-800 border border-slate-700 text-white rounded-lg p-2.5 mt-1 text-sm h-16 resize-none" />
            </div>

            {/* Info email */}
            {!editingTenant && (
              <div className="bg-blue-900/20 border border-blue-700/30 rounded-lg p-3">
                <p className="text-xs text-blue-300">Un email de bienvenue avec les identifiants de connexion sera envoye automatiquement a <strong>{form.contact_email || '...'}</strong></p>
              </div>
            )}

            <div className="flex justify-end gap-2 pt-2 border-t border-slate-700">
              <Button variant="outline" onClick={() => setShowForm(false)} className="border-slate-600 text-slate-300 hover:bg-slate-800">Annuler</Button>
              <Button onClick={handleSave} disabled={saving} className="bg-indigo-600 hover:bg-indigo-700 text-white px-6" data-testid="save-tenant-button">
                {saving ? 'Enregistrement...' : editingTenant ? 'Modifier' : 'Creer et envoyer les identifiants'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Detail Dialog */}
      <Dialog open={!!showDetail} onOpenChange={() => setShowDetail(null)}>
        <DialogContent className="bg-slate-900 border-slate-700 text-white max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Building2 className="w-5 h-5 text-indigo-400" />
              {detailData?.company_name}
            </DialogTitle>
          </DialogHeader>
          {detailData && (
            <div className="space-y-5 mt-2">
              {/* Status + Plan */}
              <div className="flex items-center gap-3 flex-wrap">
                <span className={`text-xs px-2.5 py-1 rounded-full border ${STATUS_COLORS[detailData.status] || 'bg-slate-800 text-slate-400 border-slate-700'}`}>
                  {STATUS_LABELS[detailData.status] || detailData.status}
                </span>
                <span className={`text-sm font-medium ${PLAN_COLORS[detailData.plan] || 'text-slate-400'}`}>{PLAN_LABELS[detailData.plan] || detailData.plan}</span>
                <Button size="sm" variant="outline" onClick={() => openChangePlan(detailData)} className="text-xs border-slate-600 text-slate-300 hover:bg-slate-800" data-testid="change-plan-button">
                  Changer plan
                </Button>
                <Button size="sm" onClick={() => handleImpersonate(detailData.id, detailData.company_name)} className="text-xs bg-blue-600 hover:bg-blue-700 text-white" data-testid="impersonate-button">
                  <ExternalLink className="w-3 h-3 mr-1" /> Tester
                </Button>
                {detailData.status === 'suspendu' ? (
                  <Button size="sm" onClick={() => handleReactivate(detailData.id)} className="text-xs bg-emerald-600 hover:bg-emerald-700 text-white">Réactiver</Button>
                ) : detailData.status !== 'supprime' && (
                  <Button size="sm" variant="outline" onClick={() => handleSuspend(detailData.id)} className="text-xs border-red-600 text-red-400 hover:bg-red-600/10">Suspendre</Button>
                )}
              </div>
              {/* Info grid */}
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div><span className="text-slate-500">Contact:</span> <span className="text-white ml-1">{detailData.contact_first_name} {detailData.contact_last_name}{!detailData.contact_first_name && detailData.contact_name}</span></div>
                <div><span className="text-slate-500">Email:</span> <span className="text-white ml-1">{detailData.contact_email}</span></div>
                <div><span className="text-slate-500">Telephone:</span> <span className="text-white ml-1">{detailData.contact_phone || '-'}</span></div>
                <div><span className="text-slate-500">Canton:</span> <span className="text-white ml-1">{detailData.canton ? `${detailData.canton} — ${CANTON_NAMES[detailData.canton] || ''}` : '-'}</span></div>
                <div><span className="text-slate-500">Adresse:</span> <span className="text-white ml-1">{detailData.address || '-'}, {detailData.postal_code} {detailData.city}</span></div>
                <div><span className="text-slate-500">No TVA:</span> <span className="text-white ml-1">{detailData.tva_number || '-'}</span></div>
              </div>
              {/* Subscription */}
              {detailData.subscription && (
                <Card className="bg-slate-800/50 border-slate-700 p-4">
                  <p className="text-xs text-slate-400 uppercase tracking-wider mb-2">Abonnement</p>
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <div><span className="text-slate-500">Début:</span> <span className="text-white ml-1">{formatDate(detailData.subscription.start_date)}</span></div>
                    <div><span className="text-slate-500">Fin:</span> <span className="text-white ml-1">{formatDate(detailData.subscription.end_date)}</span></div>
                    <div><span className="text-slate-500">Cycle:</span> <span className="text-white ml-1">{detailData.subscription.billing_cycle === 'yearly' ? 'Annuel' : 'Mensuel'}</span></div>
                    <div><span className="text-slate-500">Prix:</span> <span className="text-white ml-1">{formatCHF(detailData.subscription.price)}</span></div>
                  </div>
                </Card>
              )}
              {/* Payments */}
              <div>
                <p className="text-xs text-slate-400 uppercase tracking-wider mb-2">Paiements</p>
                {(detailData.payments || []).length === 0 ? (
                  <p className="text-sm text-slate-500">Aucun paiement</p>
                ) : (
                  <div className="space-y-2">
                    {detailData.payments.map((p) => (
                      <div key={p.id} className="flex items-center justify-between text-sm bg-slate-800/30 rounded-lg px-3 py-2">
                        <div>
                          <span className="text-emerald-400 font-medium">{formatCHF(p.amount)}</span>
                          <span className="text-slate-500 ml-2">{p.method}</span>
                          {p.reference && <span className="text-slate-600 ml-2">Réf: {p.reference}</span>}
                        </div>
                        <span className="text-xs text-slate-500">{formatDate(p.date)}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              {/* Logs */}
              <div>
                <p className="text-xs text-slate-400 uppercase tracking-wider mb-2">Journal d'activité</p>
                {(detailData.logs || []).length === 0 ? (
                  <p className="text-sm text-slate-500">Aucune activité</p>
                ) : (
                  <div className="space-y-1 max-h-40 overflow-y-auto">
                    {detailData.logs.map((l) => (
                      <div key={l.id} className="flex items-center gap-2 text-xs text-slate-400">
                        <span className="text-slate-600">{formatDate(l.created_at)}</span>
                        <span className="text-slate-300">{l.detail}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              {/* Actions */}
              <div className="flex justify-between pt-2 border-t border-slate-700">
                <Button size="sm" variant="outline" onClick={() => handleDelete(detailData.id)} className="text-xs border-red-700 text-red-400 hover:bg-red-600/10" data-testid="delete-tenant-button">
                  <Trash2 className="w-3 h-3 mr-1" /> Supprimer
                </Button>
                <div className="flex gap-2">
                  <Button size="sm" variant="outline" onClick={() => handleResendEmail(detailData.id, detailData.company_name)} className="text-xs border-blue-700 text-blue-400 hover:bg-blue-600/10" data-testid="resend-email-detail-btn">
                    <Mail className="w-3 h-3 mr-1" /> Renvoyer email
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => handleResetPassword(detailData.id, detailData.company_name)} className="text-xs border-amber-700 text-amber-400 hover:bg-amber-600/10" data-testid="reset-pw-detail-btn">
                    <KeyRound className="w-3 h-3 mr-1" /> Reset MDP
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => { openEdit(detailData); setShowDetail(null); }} className="text-xs border-slate-600 text-slate-300 hover:bg-slate-800">
                    <Pencil className="w-3 h-3 mr-1" /> Modifier
                  </Button>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Change Plan Dialog */}
      <Dialog open={!!showSubDialog} onOpenChange={() => setShowSubDialog(false)}>
        <DialogContent className="bg-slate-900 border-slate-700 text-white max-w-sm">
          <DialogHeader>
            <DialogTitle className="text-white">Changer d'abonnement</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <div>
              <Label className="text-slate-300 text-xs">Nouveau plan</Label>
              <Select value={subForm.plan} onValueChange={(v) => setSubForm({ ...subForm, plan: v })}>
                <SelectTrigger className="bg-slate-800 border-slate-700 text-white mt-1" data-testid="change-plan-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="starter">Starter — CHF 89/mois</SelectItem>
                  <SelectItem value="business">Business — CHF 189/mois</SelectItem>
                  <SelectItem value="enterprise">Enterprise — CHF 349/mois</SelectItem>
                  <SelectItem value="enterprise_plus">Enterprise+ — Sur demande</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-slate-300 text-xs">Cycle de facturation</Label>
              <Select value={subForm.billing_cycle} onValueChange={(v) => setSubForm({ ...subForm, billing_cycle: v })}>
                <SelectTrigger className="bg-slate-800 border-slate-700 text-white mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="monthly">Mensuel</SelectItem>
                  <SelectItem value="yearly">Annuel (2 mois offerts)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setShowSubDialog(false)} className="border-slate-600 text-slate-300">Annuler</Button>
              <Button onClick={handleChangePlan} className="bg-indigo-600 hover:bg-indigo-700 text-white" data-testid="confirm-change-plan">Confirmer</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </SuperAdminLayout>
  );
};

export default SuperAdminTenants;
